/**
 * Service Worker client — registers the SW and provides a typed postMessage API
 * to store/retrieve the bearer token. Token never lives in page JS context.
 */

const SW_URL = "/service-worker.js";

let registrationPromise: Promise<ServiceWorkerRegistration> | null = null;

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
  if (!("serviceWorker" in navigator)) {
    throw new Error("Service Workers are not supported in this browser");
  }
  if (registrationPromise) return registrationPromise;
  registrationPromise = navigator.serviceWorker
    .register(SW_URL, { scope: "/" })
    .then(async (reg) => {
      await navigator.serviceWorker.ready;
      return reg;
    });
  return registrationPromise;
}

function postAndWait<T>(msg: unknown): Promise<T> {
  return new Promise((resolve, reject) => {
    const channel = new MessageChannel();
    const sw = navigator.serviceWorker.controller;
    if (!sw) {
      reject(new Error("no active service worker"));
      return;
    }
    channel.port1.onmessage = (e) => resolve(e.data as T);
    sw.postMessage(msg, [channel.port2]);
    setTimeout(() => reject(new Error("SW postMessage timeout")), 2000);
  });
}

export async function setToken(token: string): Promise<void> {
  await registerServiceWorker();
  // Ensure SW is controlling — after first registration, it may take a cycle
  if (!navigator.serviceWorker.controller) {
    await new Promise<void>((resolve) => {
      navigator.serviceWorker.addEventListener(
        "controllerchange",
        () => resolve(),
        { once: true },
      );
    });
  }
  await postAndWait<{ ok: boolean }>({ type: "SET_TOKEN", token });
}

export async function clearToken(): Promise<void> {
  if (!navigator.serviceWorker.controller) return;
  await postAndWait<{ ok: boolean }>({ type: "CLEAR_TOKEN" });
}

export async function getToken(): Promise<string | null> {
  await registerServiceWorker();
  if (!navigator.serviceWorker.controller) return null;
  const res = await postAndWait<{ token: string | null }>({
    type: "GET_TOKEN",
  });
  return res.token;
}
